import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertTriangle, RefreshCw, Shield, Siren, Clock } from 'lucide-react';
import { computeSlaState, formatBizDuration } from '@/lib/slaClock';

const RowsTable = ({ rows, kind }) => {
  if (!rows || rows.length === 0) {
    return (
      <div className="py-10 text-center text-sm text-muted-foreground">
        Nothing to show yet.
      </div>
    );
  }
  return (
    <div className="max-h-[60vh] overflow-y-auto border rounded-md">
      <table className="w-full text-sm">
        <thead className="bg-muted/60 sticky top-0">
          <tr className="text-left">
            <th className="px-3 py-2 font-semibold">Lead</th>
            <th className="px-3 py-2 font-semibold">Phone</th>
            <th className="px-3 py-2 font-semibold">Team</th>
            <th className="px-3 py-2 font-semibold">
              {kind === 'reassigned' ? 'From → To' : 'Assigned To'}
            </th>
            <th className="px-3 py-2 font-semibold">
              {kind === 'at_risk' ? 'Elapsed / Left' : (kind === 'warned' ? 'Warned At' : 'Reassigned At')}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((l) => {
            const state = computeSlaState(l, new Date());
            return (
              <tr key={l.id} className="border-t hover:bg-muted/40" data-testid={`sla-row-${l.id}`}>
                <td className="px-3 py-2 font-medium">{l.full_name || '—'}</td>
                <td className="px-3 py-2 font-mono text-xs">{l.phone || '—'}</td>
                <td className="px-3 py-2 text-xs">{l.team_name || '—'}</td>
                <td className="px-3 py-2 text-xs">
                  {kind === 'reassigned' ? (
                    <span>
                      <span className="line-through text-muted-foreground">{l.original_agent_name || '—'}</span>
                      <span className="mx-1">→</span>
                      <span className="font-medium">{l.assigned_to_name}</span>
                    </span>
                  ) : (
                    l.assigned_to_name || '—'
                  )}
                </td>
                <td className="px-3 py-2 text-xs">
                  {kind === 'at_risk' ? (
                    <div className="flex flex-col">
                      <span>Elapsed: <b>{formatBizDuration(state.elapsedBizHours)}</b></span>
                      {state.remainingToWarn != null && (
                        <span className="text-yellow-700">Warn in {formatBizDuration(state.remainingToWarn)}</span>
                      )}
                      {state.remainingToReassign != null && (
                        <span className="text-orange-700">Reassign in {formatBizDuration(state.remainingToReassign)}</span>
                      )}
                    </div>
                  ) : (
                    <span>{new Date(
                      kind === 'reassigned' ? l.new_lead_sla_12h_reassigned_at : l.new_lead_sla_3h_warned_at
                    ).toLocaleString('en-AE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const SlaWatchdogPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drillOpen, setDrillOpen] = useState(false);
  const [drillKind, setDrillKind] = useState('at_risk');
  const [runningSweep, setRunningSweep] = useState(false);

  const fetchStatus = React.useCallback(async () => {
    try {
      const res = await api.get('/admin/new-lead-sla/status');
      setData(res.data);
    } catch (e) {
      // Silently fail — panel is optional. Log for debugging.
      console.warn('SLA panel: fetch failed', e?.response?.data || e?.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const t = setInterval(fetchStatus, 60_000);
    return () => clearInterval(t);
  }, [fetchStatus]);

  const runSweepNow = async () => {
    setRunningSweep(true);
    try {
      await api.post('/admin/new-lead-sla/run-now');
      await fetchStatus();
    } catch (_e) {
      // noop
    } finally {
      setRunningSweep(false);
    }
  };

  if (loading) return null;
  if (!data) return null;

  const t = data.today || {};
  const totals = data.totals || {};

  const openDrill = (kind) => {
    setDrillKind(kind);
    setDrillOpen(true);
  };

  const rows = drillKind === 'warned'
    ? (t.warned_leads || [])
    : drillKind === 'reassigned'
      ? (t.reassigned_leads || [])
      : (t.at_risk_leads || []);

  return (
    <>
      <Card data-testid="sla-watchdog-panel" className="border-l-4 border-l-amber-500">
        <CardContent className="p-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-md bg-amber-100 text-amber-700 flex items-center justify-center">
                <Shield className="h-4 w-4" />
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  New-Lead SLA · Today
                </div>
                <div className="text-[11px] text-muted-foreground">
                  3h warn · 12h auto-reassign · Dubai biz-hrs
                </div>
              </div>
            </div>

            <div className="ml-auto flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => openDrill('at_risk')}
                className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-left transition"
                data-testid="sla-panel-at-risk"
                title="Tracked new leads assigned since Dubai midnight — click to drill down"
              >
                <Clock className="h-4 w-4 text-emerald-700" />
                <div className="leading-tight">
                  <div className="text-[10px] uppercase text-emerald-700 font-semibold">Tracked</div>
                  <div className="text-lg font-bold text-emerald-800">{t.at_risk_count || 0}</div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => openDrill('warned')}
                className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-amber-50 hover:bg-amber-100 border border-amber-200 text-left transition"
                data-testid="sla-panel-warned"
                title="Leads warned at 3h today"
              >
                <AlertTriangle className="h-4 w-4 text-amber-700" />
                <div className="leading-tight">
                  <div className="text-[10px] uppercase text-amber-700 font-semibold">Warned</div>
                  <div className="text-lg font-bold text-amber-800">{t.warned_count || 0}</div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => openDrill('reassigned')}
                className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-red-50 hover:bg-red-100 border border-red-200 text-left transition"
                data-testid="sla-panel-reassigned"
                title="Leads auto-reassigned at 12h today"
              >
                <Siren className="h-4 w-4 text-red-700" />
                <div className="leading-tight">
                  <div className="text-[10px] uppercase text-red-700 font-semibold">Reassigned</div>
                  <div className="text-lg font-bold text-red-800">{t.reassigned_count || 0}</div>
                </div>
              </button>

              <div className="hidden md:flex items-center gap-2 px-3 border-l pl-3 ml-1">
                <div className="text-[11px] text-muted-foreground">All time:</div>
                <Badge variant="outline">Tracked {totals.tracked || 0}</Badge>
                <Badge variant="outline" className="text-amber-700 border-amber-300">Warned {totals.warned || 0}</Badge>
                <Badge variant="outline" className="text-red-700 border-red-300">Reassigned {totals.reassigned || 0}</Badge>
              </div>

              <Button
                variant="ghost"
                size="sm"
                onClick={runSweepNow}
                disabled={runningSweep}
                data-testid="sla-panel-run-now"
                title="Manually trigger a sweep now"
              >
                <RefreshCw className={`h-4 w-4 ${runningSweep ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={drillOpen} onOpenChange={setDrillOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>
              {drillKind === 'at_risk' && 'Tracked new leads · at-risk'}
              {drillKind === 'warned' && 'Warned today (3h SLA fired)'}
              {drillKind === 'reassigned' && 'Auto-reassigned today (12h SLA fired)'}
            </DialogTitle>
          </DialogHeader>
          <RowsTable rows={rows} kind={drillKind} />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SlaWatchdogPanel;
